import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { ApplicationConfig } from 'src/app/models/config';
import { NavigationSection, NavigationItem, NavigationItemChild } from 'src/app/models/navigation';
import { ApplicationService, CustomerService, DashboardService, DataService } from 'src/api/services';
import { Customer } from '../api/models/customer';
import { CustomerSummary, DistributionListEventSummary, ApplicationType, UserProfileModel } from 'src/api/models';

import { Moment } from 'moment';
import * as moment from 'moment-timezone';
import { AzureAuthService } from './azure-auth.service';

@Injectable({
  providedIn: 'root'
})
export class ApplicationConfigService {

  constructor(
    private readonly httpHandler: HttpBackend,
    private customerService: CustomerService,
    private dashboardService: DashboardService,
    private applicationService: ApplicationService,
    //private authService: AzureAuthService,
    private dataService: DataService) {

    this.http = new HttpClient(httpHandler);
    // need to init as early as possible to ensure the apiKey is set for Google Maps to work.
    this.colours = [
      '#4e9cff','#ff9f0c','#f9e80d','#798e98','#3cd2a5','#ff6060','ff9f0c','#30beff','#c0cacf','#35434a','#fff','#ff0000','#00ff00'
    ];
    this.tenantTimeZone = moment.tz.guess();
  }

  public rootStyles: HTMLStyleElement;

  public sideBarCollapsed: boolean = false;// = window.innerWidth < 400;
  public menuOpen: boolean = false;

  public enableDebug: boolean = false;

  public loggedIn: boolean = false;

  public theme: string = 'dark';

  public applicationName: string = "INDICIUM Cloud";
  public themeColour: string = "teal";
  public logoUrl: string = "indicium-small-green.svg";
  public backgroundImageUrl: string = "assets/cover/cover-5.jpg";
  public homeScreen: string = "The INDICIUM Stack provides a custom data management solution for the collection, analysis, reporting, alerting and control of data from a range of diverse monitoring applications.";

  public setApplications(user: UserProfileModel){
    this.setCustomerId(user.customerId);
  }

  public setStyles(){
    this.rootStyles.innerHTML = "html:after {background-image:url(" + this.backgroundImageUrl + ")}";
    document.body.setAttribute("class","theme-" + this.themeColour);
  }

  public setTheme(theme){
    if (theme){
      this.theme = theme;
      var htmlElm = document.documentElement;
      htmlElm.setAttribute("data-bs-theme", this.theme);
      document.body.setAttribute('class', this.theme == 'dark' ? 'highcharts-dark' : 'highcharts-light');
    }
  }

  public timeZoneFilter: string;
  public tenantTimeZone: string;

  public getTimeZones(filter, count){
    return (moment.tz.names().filter(tz => tz.toLowerCase().indexOf(filter.toLowerCase()) >= 0) as string[]).slice(0, count);
  }

  public getAllTimeZones(){
    return moment.tz.names();
  }

  public getCurrentTimeZone(){
    return this.tenantTimeZone;
  }

  public getTimeZoneOffset(tz:string){
    if (tz){
      return - moment.tz.zone(tz).utcOffset(Date.now());
    }else{
      return - moment.tz.zone(this.tenantTimeZone).utcOffset(Date.now());
    }
  }

  public setTenantTimeZone(tenantTz: string) {
    console.log("Setting TimeZone", tenantTz);
    this.tenantTimeZone = tenantTz;
  }

  public customerId: number;
  public applications: ApplicationType[];
  public notifications: DistributionListEventSummary[];
  public customers: CustomerSummary[];
  public customer: CustomerSummary;

  public setCustomerId(id: number){
    console.log("Setting CustomerId", id);
    if (this.customerId != id || this.customer == null || this.customer.customerId != id){
      console.log("Customer Changed", id);
      this.customerId = id;
      if (this.customers){
        this.customer = this.customers.find(c => c.customerId == id);
        this.homeScreen = this.customer.homeScreen;
        this.applicationName = this.customer.customerName;
        this.homeScreen = this.customer.homeScreen;
        this.themeColour = this.customer.themeColour;
        this.logoUrl = this.customer.logoUrl;
        this.backgroundImageUrl = this.customer.backgroundImageUrl;

        this.rootStyles.innerHTML = "html:after {background-image:url(" + this.backgroundImageUrl + ")}";

        // todo: logos and other things need to be updated from the RegisterUser call - which won't work for a change customer
        this.setDashboardMenu();
        this.setSiteTypeMenu(this.customerId);
        // load the defaults for this customer - logo/icons etc
      }
      this.loadApplications();
      // update the site types?

      this.customerChangedSource.next(id);
    }
  }

  public loadApplications(){
    if (this.customerId != null){
      this.applicationService.ApplicationGetAllActive(this.customerId).subscribe(r => {
        this.applications = r;
        this.refreshMenu()
      });
    }
  }

  private customerChangedSource: Subject<number> = new Subject<number>();
  customerChanged$ = this.customerChangedSource.asObservable();

  public apiKey: string;
  public settings: ApplicationConfig = new ApplicationConfig();
  private http: HttpClient;

  public sections: Array<NavigationSection>;
  public _sections: Array<NavigationSection> = [
  { name: "", pages: [
      { name: 'Home', icon: 'bi bi-house-door', route: '/home', isAdmin: false, isOwner: false, role: null, featureName: null, applicationName: null, hidden: false, visible: true, toggled: false },
      { name: 'Analysis', icon: 'bi bi-graph-up', route: '/data/analysis', isAdmin: false, isOwner: false, role: null, featureName: "analysis", applicationName: null, hidden: false, visible: true, toggled: false},
      { name: 'Dashboards', icon: 'bi bi-grid-1x2', route: '/dashboards', isAdmin: false, isOwner: false, role: null, featureName: "dashboards", applicationName: null, hidden: false, visible: true, toggled: false, children: [
        { name: 'Add New', route: '/dashboard/new', external: false, featureName: null, role: null }
      ]},


    ]},

    { name: "Applications", pages: [
      { name: 'Fire Foresight', icon: 'bi bi-fire', route: '/fireforesight', isAdmin: false, isOwner: false, role: null, featureName: null, applicationName: 'fire-foresight', hidden: false, visible: false, toggled: false, children: [
        //{ name: 'Command Centre', route: '/fire-foresight/command', external: false, featureName:null },
        { name: 'Cameras', route: '/fire-foresight/cameras', external: false, featureName:null, role: null },
        { name: 'Incidents', route: '/fire-foresight/incidents', external: false, featureName:null, role: null },
        { name: 'Watch', route: '/fire-foresight/watch', external: false, featureName:null, role: null },
        { name: 'Statistics', route: '/fire-foresight/statistics', external: false, featureName:null, role: null, isAdmin: true },
      ]},
      { name: 'Burn Lists', icon: 'bi bi-list-stars', route: '/fireforesight', isAdmin: false, isOwner: true, role: null, featureName: null, applicationName: 'fire-foresight', hidden: false, visible: false, toggled: false, children: [
        { name: 'Burn Lists', route: '/fire-foresight/burn-list', external: false, featureName:null, role: null },
      ]},

      { name: 'Pipeline Control', icon: 'bi bi-droplet', route: '/eucs', isAdmin: false, isOwner: false, role: null, featureName: "eucs", applicationName: 'eucs', hidden: false, toggled: false, visible: false, children: [
        { name: 'Dashboard', route: '/eucs/dashboard', external: false, featureName:null, role: null }
      ]},

      { name: 'Smart Irrigation', icon: 'bi bi-droplet', route: '/smart-irrigation', isAdmin: false, isOwner: false, role: 'irrigation', featureName: null, applicationName: 'smart-irrigation', hidden: false, toggled: false, visible: false, children: [
        { name: 'Controllers', route: '/smart-irrigation/controllers', external: false, featureName: null, role: 'irrigation-manager' },
        { name: 'Regions', route: '/smart-irrigation/regions', external: false, featureName: null, role: null },
        { name: 'Parks', route: '/smart-irrigation/parks', external: false, featureName: null, role: null },
        //{ name: 'Schedules', route: '/smart-irrigation/schedules', external: false, featureName:null }
        { name: 'Admin', route: '/smart-irrigation/admin', external: false, featureName: null, role: 'irrigation-admin' },
        { name: 'System Health', route: '/smart-irrigation/system-health', external: false, featureName: null, role: 'irrigation-admin' }
      ]},
    ]},

    { name: "Devices", pages: [
      { name: 'Map', icon: 'bi bi-map', route: '/sites', isAdmin: false, isOwner: false, role: null, featureName: "dashboards", applicationName: null, hidden: false, visible: true, toggled: false, children: []},
      { name: 'Sites', icon: 'bi bi-stack', isAdmin: false, isOwner: false, role: null, featureName: "dashboards", applicationName: null, hidden: false, toggled: false, visible: true, children: []},
      { name: 'Camera Watch', icon: 'bi bi-camera-video', route: '/camera-watch', isAdmin: false, isOwner: false, role: null, featureName: "dashboards", applicationName: null, hidden: false, visible: false, toggled: false, children: []}
    ]},



    { name: "Marketplace", pages: [
      { name: 'DataBus', icon: 'bi bi-diagram-2', isAdmin: true, isOwner: true, featureName: "databus", applicationName: null, role: null, hidden: false, visible: true, toggled: false, children: [
        { name: 'Licenses', external: false, route: '/databus', featureName:"databus", role: null, isAdmin: true },
        { name: 'Purchase', external: false, route: '/databus/purchase', featureName:"databus", role: null, isAdmin: true },
        { name: 'Approve', external: false, route: '/databus/approve', featureName:"databus", role: null, isAdmin: true },
        { name: 'Health', external: false, route: '/databus/health', featureName:"databus", role: null, isAdmin: true }
      ]},

      { name: 'Applications', icon: 'bi bi-minecart', route: '/marketplace', isAdmin: true, isOwner: true,  role: null, featureName: "dashboards", applicationName: null, hidden: false, visible: true, toggled: false},

      //{ name: 'Support', icon: 'fa-bell', route: '/support', isAdmin: false, role: "support", featureName: "support", hidden: false, toggled: false },
    ]},

    { name: "Settings", pages: [
      { name: 'Customers', icon: 'bi bi-building', route: '/customers', isAdmin: true, isOwner: false, role: "admin", featureName: null, applicationName: null, hidden: false, visible: true, toggled: false},
      { name: 'Distribution List', icon: 'bi bi-people', route: '/distribution', isAdmin: false,  role: "distributionEditor", featureName: "dashboards", applicationName: null, hidden: false, visible: true, toggled: false },
      { name: 'Organisation Settings', icon: 'bi bi-gear', route: '/settings', isAdmin: false, isOwner: true, role: null, featureName: "products", applicationName: null, hidden: false, visible: true, toggled: false},
      { name: 'Administration', icon: 'bi bi-sliders', isAdmin: true, featureName: null, applicationName: null, role: "admin", hidden: false, toggled: false, visible: true, children: [
        { name: 'API', external: true, route: '/swagger/index.html', featureName:null, role: null, isAdmin: true },
        { name: 'User Statistics', external: false, route: '/user-statistics', featureName:null, role: null, isAdmin:true },
        { name: 'Users', route: '/users', external: false, featureName:null, role: null, isAdmin:true },
        { name: 'Sites', route: '/site-types', external: false, featureName: null, role: null, isAdmin:true },
        { name: 'Products', route: '/products', external: false, featureName:"products", role: null, isAdmin:true },
        { name: 'Map Layers', route: '/map-layers', external: false, featureName: null, role: null, isAdmin:true },
        { name: 'Geo Assets', route: '/geo-assets', external: false, featureName: null, role: null, isAdmin:true },
        { name: 'Widget Templates', route: '/admin/widget-text-templates', external: false, featureName: null, role: null, isAdmin:true },
        
        { name: 'Control', route: '/control', external: false, featureName: "products", role: null, isAdmin:true },
        { name: 'Fire Foresight', route: '/fire-foresight/admin', external: false, featureName: 'fire-foresight', role: null, isAdmin:true },
        { name: 'Smart Irrigation', route: '/smart-irrigation/admin', external: false, featureName: 'smart-irrigation', role: null, isAdmin:true }
      ]},
    ]},

    { name: "Support", pages: [
      { name: 'My Tickets', icon: 'far fa-question-circle', route: '/support/home', isAdmin: false, isOwner: false, role: null, featureName: "support", applicationName: null, hidden: false, visible: true, toggled: false, children: []},
      { name: 'Ticket Search', icon: 'fa fa-search', route: '/support/search', isAdmin: true, isOwner: false, role: null, featureName: "support", applicationName: null, hidden: false, visible: true, toggled: false, children: []},
    ]}
  ];

  init(): Promise<boolean> {
    // if firstUrl is set, then redirect to that URL and clear it
    console.log("AppConfig.Init()");
    return new Promise<boolean>((resolve, reject) => {
      this.http.get('/api/config/GetApplicationConfig').pipe(map(res => res))
        .subscribe((res: any) => {
          console.log("GetApplicationConfig Complete", this.settings);
          this.apiKey = res.googleMapsApiKey;
          this.settings.version = res.version;
          this.settings.googleMapsApiKey = res.googleMapsApiKey;
          this.settings.authMode = res.authMode;
          this.settings.azureTenantId = res.azureTenantId;
          this.settings.azureClientId = res.azureClientId;
          this.settings.azureRedirectUrl = res.azureRedirectUrl;
          this.settings.features = res.features;
          this.settings.latestReleaseNotesId = res.latestReleaseNotesId;
          this.settings.welcome = res.latestTermsAndConditions.welcome;
          this.settings.agreement = res.latestTermsAndConditions.agreement;
          var sections = [];
          for (let section of this._sections){
            var sec : NavigationSection = {name:section.name,pages:[]};
            sections.push(sec);
            for (let page of section.pages){

              if (page.featureName == null || this.settings.features.indexOf(page.featureName) >= 0) {
                // sub features
                if (page.children){
                  var children = page.children;
                  page.children = [];
                  for (let subpage of children){
                    if (subpage.featureName != null && this.settings.features.indexOf(subpage.featureName) >= 0) {
                      page.children.push(subpage);
                    }
                    else{
                      page.children.push(subpage);
                    }
                  }
                }
                sec.pages.push(page);
              }
            }
          }
          this.sections = sections;
          console.log(this.sections);
          resolve(true);
        },
        (error) => {
          console.error("GetApplicationConfig Error", error);
          reject(error);
        });
    });
  }

  private refreshMenu(){
    for (let section of this.sections){
      for (let page of section.pages){
        page.visible = ((page.featureName == null && page.applicationName == null) || this.featureAllowed(page.featureName) || this.applicationAllowed(page.applicationName));//&& this.authService.canViewPage(page);
        //page.applicationName == null || this.applications.find(a => a.applicationUri == page.applicationName) != null;
      }
    }
  }

  featureAllowed(featureName) : boolean{
    return featureName != null && this.settings.features.indexOf(featureName) >= 0
  }

  applicationAllowed(applicationName) : boolean{
    return applicationName != null && this.applications != null && this.applications.find(a => a.applicationUri == applicationName) != null;
  }

  setCustomersMenu(customerId: number) : void{
    this.customerService.CustomerGetAllCustomers()
      .subscribe(res =>{
          this.customers = res;
          this.customerId = customerId
          if (this.customers.length == 1){
            this.customer = res[0];
          }
          else{
            this.customer = this.customers.find(c => c.customerId == customerId);
          }
          this.loadApplications()
      });
  }

  /**
   * Add list of dashboards to navigation
   */
  setDashboardMenu(): void {
    console.log('setDashboardMenu()');
    this.dashboardService.DashboardGetMyDashboards()
      .subscribe(
        res => {
          // Map dashboard information into valid Navigation Item Children
          const dashboardMenu = this.findPage(this.sections, "Dashboards");
          const dashboardMenuItems: Array<NavigationItemChild> = res.map(dashboard => {
            return { name: dashboard.name, external: false, route: '/dashboard/' + dashboard.dashboardId, featureName: null, applicationName: null, role: null }
          });

          // clear any existing items
          dashboardMenu.children = [];

          // Populate with new create button, and new items.
          //dashboardMenu.children.push({ name: 'Create New', external: false, route: '/dashboard/new', featureName: null }, ...dashboardMenuItems);
          dashboardMenu.children.push(...dashboardMenuItems);
        }
      )
  }

  findPage(sections:NavigationSection[], name:string) : NavigationItem {
    for (let section of sections){
      if (section.pages){
        var page = section.pages.find(page => page.name === name);
        if (page) return page;
      }
    }
    return null;
  }

  /**
 * Add list of dashboards to navigation
 */
  setSiteTypeMenu(customerId: number): void {
    console.log('setSiteTypeMenu()');
    this.dataService.DataGetSiteTypes(customerId)
      .subscribe(
        res => {
          const siteTypesMenu = this.findPage(this.sections, "Sites");
          if (res == null || res.length == 0)
          {
            //dashboardMenu.hidden = true;
          }
          else {
            // Map dashboard information into valid Navigation Item Children
            const siteTypesMenuItems: Array<NavigationItemChild> = res.map(st => {
              return { name: st.siteTypeCode, external: false, route: '/site-type/' + st.siteTypeId, featureName: null, applicationName: null, role: null }
            });

            // clear any existing items
            siteTypesMenu.children = [];

            // Populate with new create button, and new items.
            //siteTypesMenu.children.push({ name: 'Create New', external: false, route: '/dashboard/new', featureName: null }, ...dashboardMenuItems);
            siteTypesMenu.children.push(...siteTypesMenuItems);
          }

          // Check if user has camera site types (excluding "Fire Camera")
          const hasCameraSiteTypes = res && res.some(st =>
            st.siteTypeCode &&
            st.siteTypeCode.toLowerCase().includes('camera') &&
            st.siteTypeCode.toLowerCase() !== 'fire camera'
          );

          // Set Camera Watch menu item visibility
          const cameraWatchMenu = this.findPage(this.sections, "Camera Watch");
          if (cameraWatchMenu) {
            cameraWatchMenu.visible = hasCameraSiteTypes;
            console.log('Camera Watch menu visibility set to:', hasCameraSiteTypes);
          }
        }
      )
  }

  getSettings(): ApplicationConfig {
      if (this.settings) { return this.settings; }
      return null;
  }

  colours: string[];


}
